import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep, isNumber } from "lodash";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { Contract } from "../../customProvider/objects/contract";
import { Picture } from "../../picture/objects/picture";
import { ADD_CUSTOM_PICTURES, SET_PICTURE } from "../../picture/redux/actionTypes";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_CONTRACT_PARAMETERS, EMPTY_MANUAL_ACTIVITY, EMPTY_MANUAL_ACTIVITY_LIST_CONFIGURATION, EMPTY_MANUAL_ACTIVITY_LIST_CRITERIA } from "../constant/manualActivityConstant";
import { CancellationRule } from "../objects/cancellationRule";
import { ManualActivity } from "../objects/manualActivity";
import { ManualActivityContractParameters } from "../objects/manualActivityContractParameters";
import { ManualActivityListCriteria } from "../objects/manualActivityListCriteria";
import { ManualActivityPrice } from "../objects/manualActivityPrice";
import { ManualActivityState } from "../objects/manualActivityState";
import { ManualActivityVariant } from "../objects/manualActivityVariant";
import { setManualActivityPreference } from "../utils/setManualActivityPreference";
import { Column } from "../objects/column";
import { transformActivityFromBackend, transformVariantFromBackend } from "../utils/transformActivity";
import { Cms } from "../../cms/objects/cms";


const initialState: ManualActivityState = {
    manual_activity_list: [],
    selected_activity: [],
    manual_activity_list_configuration: EMPTY_MANUAL_ACTIVITY_LIST_CONFIGURATION,
    manual_activity_list_loading: false,
    manual_activity_list_count: 0,
    manual_activity_list_criteria: EMPTY_MANUAL_ACTIVITY_LIST_CRITERIA,
    manual_activity: EMPTY_MANUAL_ACTIVITY,
    manual_activity_title: {},
    selected_contract: null,
    manual_activity_cancellation_rules: [],
    manual_activity_cancellation_rules_loading: false,
    manual_activity_cancellation_rule: null,
    picture: null,
    pictures: [],
    pictures_order: [],
    selected_variant: null,
    manual_activity_contract_parameter: EMPTY_CONTRACT_PARAMETERS,
    vat: 0,
    manual_activity_prices: [],
    catalog_price: { id: -1, catalog_price: false },
    price_by_schedule: { id: -1, price_by_schedule: false },
    margin_master_markup: false,
    first_preference_loaded: false,
    allowed_cms: []
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const setPicture = createAction<boolean | Picture>(SET_PICTURE);
const addPictures = createAction<Picture[]>(ADD_CUSTOM_PICTURES);

const setSelectedActivityReducer = (state: ManualActivityState, action: PayloadAction<ManualActivity[]>): ManualActivityState => ({
    ...state, selected_activity: action.payload
});

const addManualActivityListConfigurationPreferenceReducer = (state: ManualActivityState, action: PayloadAction<Preferences[]>): ManualActivityState => ({
    ...state, manual_activity_list_configuration: { ...state.manual_activity_list_configuration, preferences: [...state.manual_activity_list_configuration.preferences, ...action.payload] }
});

const setManualActivityListConfigurationPreferenceReducer = (state: ManualActivityState, action: PayloadAction<Preferences[]>): ManualActivityState => ({
    ...state, manual_activity_list_configuration: { ...state.manual_activity_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: ManualActivityState, action: PayloadAction<Preferences[]>): ManualActivityState => {
    const { preferences, columns, order_by, order } = setManualActivityPreference(action.payload, state.manual_activity_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        manual_activity_list_configuration: { ...state.manual_activity_list_configuration, preferences, columns },
        manual_activity_list_criteria: { ...state.manual_activity_list_criteria, order, order_by }
    };
};

const moveDownColumnReducer = (state: ManualActivityState, action: PayloadAction<number>): ManualActivityState => {
    const columns = cloneDeep([...state.manual_activity_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, manual_activity_list_configuration: { ...state.manual_activity_list_configuration, columns } };
};


const moveUpColumnReducer = (state: ManualActivityState, action: PayloadAction<number>): ManualActivityState => {
    const columns = cloneDeep([...state.manual_activity_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, manual_activity_list_configuration: { ...state.manual_activity_list_configuration, columns } };
};

const toggleColumnReducer = (state: ManualActivityState, action: PayloadAction<number>): ManualActivityState => {
    const columns = cloneDeep([...state.manual_activity_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, manual_activity_list_configuration: { ...state.manual_activity_list_configuration, columns } };
};

const updateManualActivityListReducer = (state: ManualActivityState, action: PayloadAction<ManualActivity[]>): ManualActivityState => ({
    ...state, manual_activity_list: action.payload
});
const updateManualActivityListCriteriaReducer = (state: ManualActivityState, action: PayloadAction<ManualActivityListCriteria>): ManualActivityState => ({
    ...state, manual_activity_list_criteria: action.payload
});
const updateManualActivityReducer = (state: ManualActivityState, action: PayloadAction<ManualActivity>): ManualActivityState => ({
    ...state,
    manual_activity: { ...state.manual_activity, ...transformActivityFromBackend(action.payload) },
    picture: isNumber(action.payload.picture) ? null : action.payload.picture || null,
    pictures: action.payload.pictures as Picture[] || [],
    pictures_order: action.payload && action.payload.pictures !== null
        ? [...action.payload.pictures as Picture[]]
        : [],
});
const updateManualActivityListLoadingReducer = (state: ManualActivityState, action: PayloadAction<boolean>): ManualActivityState => ({
    ...state, manual_activity_list_loading: action.payload
});
const updateManualActivityListCountReducer = (state: ManualActivityState, action: PayloadAction<number>): ManualActivityState => ({
    ...state, manual_activity_list_count: action.payload
});

const updatetitleReducer = (state: ManualActivityState, action: PayloadAction<any>): ManualActivityState => ({
    ...state, manual_activity_title: isNumber(action.payload.id) ? { ...state.manual_activity_title, [action.payload.id]: action.payload.value } : {}
});

const updateSelectedContractReducer = (state: ManualActivityState, action: PayloadAction<null | Contract>): ManualActivityState => ({
    ...state, selected_contract: action.payload
});

const addManualActivityCancellationRuleReducer = (state: ManualActivityState, action: PayloadAction<CancellationRule>): ManualActivityState => ({
    ...state, manual_activity_cancellation_rules: [{ ...action.payload, periods: action.payload?.periods?.map(period => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) || [] }, ...state.manual_activity_cancellation_rules]
});
const updateManualActivityCancellationRuleReducer = (state: ManualActivityState, action: PayloadAction<CancellationRule>): ManualActivityState => ({
    ...state, manual_activity_cancellation_rules: state.manual_activity_cancellation_rules.map((cancellation_rule) => cancellation_rule.id === action.payload.id ? { ...action.payload, periods: action.payload.periods.map(period => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) } : cancellation_rule)
});
const updateManualActivityCancellationRulesReducer = (state: ManualActivityState, action: PayloadAction<CancellationRule[]>): ManualActivityState => ({
    ...state, manual_activity_cancellation_rules: action.payload.map((rule) => ({ ...rule, periods: rule?.periods?.map(period => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) || [] }))
});
const deleteManualActivityCancellationRuleReducer = (state: ManualActivityState, action: PayloadAction<number>): ManualActivityState => ({
    ...state, manual_activity_cancellation_rules: state.manual_activity_cancellation_rules.filter((cancellation_rule) => cancellation_rule.id !== action.payload)
});

const deletePictureReducer = (state: ManualActivityState): ManualActivityState => ({
    ...state, picture: null
});

const deletePicturesReducer = (
    state: ManualActivityState,
    action: PayloadAction<number>
): ManualActivityState => ({
    ...state,
    pictures: state.pictures.filter(
        (element, index) => index !== action.payload
    )
});

const addPicturesReducer = (
    state: ManualActivityState,
    action: PayloadAction<any>
): ManualActivityState => ({
    ...state, pictures: [...state.pictures, ...action.payload]
});

const setPictureReducer = (
    state: ManualActivityState,
    action: PayloadAction<any>
): ManualActivityState =>
    action.payload !== true && action.payload !== false
        ? {
            ...state,
            pictures: state.pictures.map(
                (picture) =>
                    picture.id === (action.payload as Picture).id
                        ? (action.payload as Picture)
                        : picture
            )
        }
        : { ...state };


const updatePicturesOrderReducer = (
    state: ManualActivityState,
    action: PayloadAction<{ drag_index: number; hover_index: number }>
): ManualActivityState => ({
    ...state,
    pictures_order: state.pictures_order.map((item, index) => {
        if (index === action.payload.drag_index) {
            return state.pictures_order[action.payload.hover_index];
        } else if (index === action.payload.hover_index) {
            return state.pictures_order[action.payload.drag_index];
        }
        return item;
    })
});

const setSelectedVariantReducer = (state: ManualActivityState, action: PayloadAction<ManualActivityVariant | null>): ManualActivityState => ({
    ...state, selected_variant: action.payload ? ({ ...action.payload, schedules: action.payload.schedules.map(schedule => ({ ...schedule, periods: schedule.periods?.map(period => ({ ...period, start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) || [] })) }) : null,
    pictures: action.payload?.pictures as Picture[] || [],
    pictures_order: action.payload && action.payload.pictures
        ? [...action.payload.pictures as Picture[]]
        : [],
});

const updateManualActivityVariantReducer = (state: ManualActivityState, action: PayloadAction<ManualActivityVariant>): ManualActivityState => ({
    ...state,
    manual_activity: { ...state.manual_activity, variants: state.manual_activity.variants.map(variant => action.payload.id === variant.id ? transformVariantFromBackend(action.payload) : variant) },
    selected_variant: state.selected_variant?.id === action.payload.id ? action.payload : state.selected_variant,
    pictures: action.payload?.pictures as Picture[] || state.pictures,
    pictures_order: action.payload && action.payload.pictures
        ? [...action.payload.pictures as Picture[]]
        : state.pictures_order,
});

const addManualActivityVariantReducer = (state: ManualActivityState, action: PayloadAction<ManualActivityVariant>): ManualActivityState => ({
    ...state, manual_activity: { ...state.manual_activity, variants: [...state.manual_activity.variants, action.payload] }
});



const setManualActivityContractParametersReducer = (state: ManualActivityState, action: PayloadAction<ManualActivityContractParameters>): ManualActivityState => ({
    ...state, manual_activity_contract_parameter: action.payload
});

const updateVatReducer = (state: ManualActivityState, action: PayloadAction<number>): ManualActivityState => ({
    ...state, vat: action.payload
});


const updateMarginMasterMarkupReducer = (state: ManualActivityState, action: PayloadAction<boolean>): ManualActivityState => ({
    ...state, margin_master_markup: action.payload
});

const setManualActivityPricesReducer = (state: ManualActivityState, action: PayloadAction<ManualActivityPrice[]>): ManualActivityState => ({
    ...state, manual_activity_prices: action.payload
});

const setCatalogPriceReducer = (state: ManualActivityState, action: PayloadAction<{ id: number, catalog_price: boolean }>): ManualActivityState => ({
    ...state, catalog_price: action.payload
});

const setPriceScheduleReducer = (state: ManualActivityState, action: PayloadAction<{ id: number, price_by_schedule: boolean }>): ManualActivityState => ({
    ...state, price_by_schedule: action.payload
});

const setColumnsReducer = (state: ManualActivityState, action: PayloadAction<Column[]>): ManualActivityState => ({
    ...state, manual_activity_list_configuration: { ...state.manual_activity_list_configuration, columns: action.payload }
});

const updateAllowedCmsReducer = (state: ManualActivityState, action: PayloadAction<Cms[]>): ManualActivityState => ({
    ...state, allowed_cms: action.payload
});

const resizeColumnReducer = (state: ManualActivityState, action: PayloadAction<ColumnResizePayload>): void => {
    state.manual_activity_list_configuration.columns = applyColumnResize(state.manual_activity_list_configuration.columns, action.payload);
};

export const manualActivity = createSlice({
    name: 'manual_activity',
    initialState,
    reducers: {
        setSelectedActivity: setSelectedActivityReducer,
        addManualActivityListConfigurationPreference: addManualActivityListConfigurationPreferenceReducer,
        setManualActivityListConfigurationPreference: setManualActivityListConfigurationPreferenceReducer,
        moveDownColumn: moveDownColumnReducer,
        moveUpColumn: moveUpColumnReducer,
        toggleColumn: toggleColumnReducer,
        updateManualActivityList: updateManualActivityListReducer,
        updateManualActivityListCriteria: updateManualActivityListCriteriaReducer,
        updateManualActivity: updateManualActivityReducer,
        updateManualActivityListLoading: updateManualActivityListLoadingReducer,
        updateManualActivityListCount: updateManualActivityListCountReducer,
        updateTitle: updatetitleReducer,
        updateSelectedContract: updateSelectedContractReducer,
        addManualActivityCancellationRule: addManualActivityCancellationRuleReducer,
        updateManualActivityCancellationRule: updateManualActivityCancellationRuleReducer,
        updateManualActivityCancellationRules: updateManualActivityCancellationRulesReducer,
        deleteManualActivityCancellationRule: deleteManualActivityCancellationRuleReducer,
        deletePicture: deletePictureReducer,
        deletePictures: deletePicturesReducer,
        updatePicturesOrder: updatePicturesOrderReducer,
        setSelectedVariant: setSelectedVariantReducer,
        updateManualActivityVariant: updateManualActivityVariantReducer,
        addManualActivityVariant: addManualActivityVariantReducer,
        setManualActivityContractParameters: setManualActivityContractParametersReducer,
        updateVat: updateVatReducer,
        updateMarginMasterMarkup: updateMarginMasterMarkupReducer,
        setManualActivityPrices: setManualActivityPricesReducer,
        setCatalogPrice: setCatalogPriceReducer,
        setPriceSchedule: setPriceScheduleReducer,
        setColumns: setColumnsReducer,
        updateAllowedCms: updateAllowedCmsReducer,
        resizeColumn: resizeColumnReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer)
            .addCase(addPictures, addPicturesReducer)
            .addCase(setPicture, setPictureReducer);
    }
});

export const {
    setSelectedActivity,
    addManualActivityListConfigurationPreference,
    setManualActivityListConfigurationPreference,
    moveDownColumn,
    moveUpColumn,
    toggleColumn,
    updateManualActivityList,
    updateManualActivityListCriteria,
    updateManualActivity,
    updateManualActivityListLoading,
    updateManualActivityListCount,
    updateTitle,
    updateSelectedContract,
    addManualActivityCancellationRule,
    updateManualActivityCancellationRule,
    updateManualActivityCancellationRules,
    deleteManualActivityCancellationRule,
    deletePicture,
    deletePictures,
    updatePicturesOrder,
    setSelectedVariant,
    updateManualActivityVariant,
    addManualActivityVariant,
    setManualActivityContractParameters,
    updateVat,
    updateMarginMasterMarkup,
    setManualActivityPrices,
    setPriceSchedule,
    setCatalogPrice,
    setColumns,
    updateAllowedCms,
    resizeColumn
} = manualActivity.actions;